import { createNoiseBuffer, createReverb, type NoiseKind } from './synth';

export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

/** Where the ears are, in world units; read from the active camera whenever a sound is placed. */
export interface ListenerPose {
  position: Vec3Like;
  forward: Vec3Like;
  right: Vec3Like;
  /** Metres within which a placed sound plays at full volume; it halves with every doubling beyond. */
  reference: number;
}

/**
 * The listener's reference in a duel: from the ears to the middle of the arena, so its fighters
 * are heard at full strength from wherever the duel's camera stands and panned by where they
 * stand, and never less than three metres. The arena on the table in the headset is all within
 * that; on a screen the camera stands tens of metres back (client-audio-engine:bugs#1).
 */
export function duelReference(ears: Vec3Like, centre: Vec3Like): number {
  return Math.max(3, Math.hypot(ears.x - centre.x, ears.y - centre.y, ears.z - centre.z));
}

export type VoicePriority = 'low' | 'normal' | 'high';

export interface VoiceOptions {
  /** World position; left out, the sound plays at the listener (interface sounds, the player's own body). */
  at?: Vec3Like;
  /** Loudness 0 … 1 before placement. */
  gain?: number;
  /** How much of the sound goes into the room reverb, 0 … 1. */
  reverb?: number;
  /** When the bus is full a new sound steals the oldest of equal or lower priority, or is dropped. */
  priority?: VoicePriority;
}

/** One slot on the bus: what a sound effect or an utterance builds its nodes into. */
export interface Voice {
  readonly context: BaseAudioContext;
  /** Connect sources (or their filters) here. */
  readonly input: AudioNode;
  /** The audio clock when the voice was taken; schedule everything relative to it. */
  readonly now: number;
  /** Registers a source so it can be silenced if the voice is stolen or stopped early. */
  track<T extends AudioScheduledSourceNode>(source: T): T;
  /** Shared noise, two seconds long; loop it for anything longer. */
  noise(kind: NoiseKind): AudioBuffer;
  /** Tells the bus when the sound has ended so the slot is free again. */
  end(at: number): void;
  /** Moves a playing sound. */
  place(at: Vec3Like): void;
  /** Fades out over `release` seconds and frees the slot. */
  stop(release?: number): void;
}

export interface LaneOptions {
  /** Loudness 0 … 1. */
  gain?: number;
  /** How much of the lane goes into the room reverb, 0 … 1. */
  reverb?: number;
  /**
   * Past the master volume and mute, straight to the limiter: for a sound the Sound slider does
   * not govern, the ambience, which follows the Music slider (the owner, 2026-09-26).
   */
  besideMaster?: boolean;
}

/** A permanent way into the master mix for something that plays for minutes (the radio). */
export interface Lane {
  readonly context: BaseAudioContext;
  /** Connect the stream here; its gain is the lane's volume. */
  readonly input: GainNode;
  /** False once the context behind the lane is gone (the bus was disposed). */
  readonly alive: boolean;
  dispose(): void;
}

export interface AudioBusOptions {
  /** Simultaneous sounds; a Quest 2 copes with about eight. */
  maxVoices?: number;
  /** Where the context comes from: tests inject a fake, a browser without Web Audio gets none. */
  createContext?: () => AudioContext | null;
}

// The ends of a touch too: a tap's touchstart and pointerdown carry no user activation yet, so
// the first tap on a phone or the Quest's page stayed silent (client-audio-engine:bugs#6).
const GESTURES = [
  'pointerdown',
  'pointerup',
  'keydown',
  'touchstart',
  'touchend',
  'click',
] as const;
/**
 * A slider's 0 … 1 as the gain the ear hears in even steps: its square. The sound slider was
 * linear and the music's squared, so at one setting the two were 7 dB apart
 * (client-audio-engine:quality#8).
 */
export function perceptualGain(value: number): number {
  const v = Math.min(1, Math.max(0, value));
  return v * v;
}
const RANK: Record<VoicePriority, number> = { low: 0, normal: 1, high: 2 };
const DEFAULT_MAX_VOICES = 8;
/** Panning stays partial so a sound at the edge of the view still reaches both ears. */
const PAN_WIDTH = 0.7;
const STEAL_RELEASE = 0.02;
/** A voice that never reports its end is freed after this long. */
const SLOT_LIFETIME = 10;
const NOISE_SECONDS = 2;

interface Slot {
  label: string;
  priority: VoicePriority;
  started: number;
  endsAt: number;
  stop(release: number): void;
  free(): void;
}

function defaultContext(): AudioContext | null {
  if (typeof AudioContext === 'undefined') return null;
  return new AudioContext({ latencyHint: 'interactive' });
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * The one audio output of the game. The context is created lazily and resumed on the first user
 * gesture, because browsers refuse to play before one; until then every `voice()` returns null
 * and the game is simply silent. All sound goes through master volume, mute and a limiter, and
 * no more than `maxVoices` sounds play at once – the oldest is faded out to make room, which
 * keeps a duel within what the Quest 2 can mix.
 */
export class AudioBus {
  private context: AudioContext | null = null;
  private failed = false;
  private master: GainNode | null = null;
  private mute: GainNode | null = null;
  private limiter: AudioNode | null = null;
  private reverb: { input: AudioNode } | null = null;
  private readonly noiseBuffers = new Map<NoiseKind, AudioBuffer>();
  private slots: Slot[] = [];
  private volume = 1;
  private muted = false;
  private listener: (() => ListenerPose | null) | null = null;
  private readonly started = new Set<() => void>();
  private readonly maxVoices: number;
  private readonly createContext: () => AudioContext | null;

  constructor(options: AudioBusOptions = {}) {
    this.maxVoices = Math.max(1, options.maxVoices ?? DEFAULT_MAX_VOICES);
    this.createContext = options.createContext ?? defaultContext;
  }

  /** True once a context exists and is allowed to play. */
  get running(): boolean {
    return this.context?.state === 'running';
  }

  /** The audio clock in seconds; 0 until a context exists. */
  get now(): number {
    return this.context?.currentTime ?? 0;
  }

  /** Listens for the gestures browsers require before sound may play; returns the detach. */
  attach(target: EventTarget): () => void {
    const unlock = () => this.unlock();
    for (const type of GESTURES)
      target.addEventListener(type, unlock, { capture: true, passive: true });
    return () => {
      for (const type of GESTURES) target.removeEventListener(type, unlock, { capture: true });
    };
  }

  /** Creates the context if there is none yet and asks it to run. Safe to call at any time. */
  unlock(): void {
    const fresh = !this.context;
    const ctx = this.ensureContext();
    if (!ctx) return;
    if (ctx.state !== 'running') void this.resume(ctx).catch(() => undefined);
    // One made in the gesture runs from the start, and is never resumed: it is announced as made.
    else if (fresh) void Promise.resolve().then(() => this.announce(ctx));
  }

  /**
   * Calls `listener` whenever the context starts to run: once the first gesture has let sound
   * play, which a context does a moment after it is asked. What plays for as long as nothing else
   * does, the table's ambience, starts then rather than at the next thing to happen. Returns the
   * detach.
   */
  onStarted(listener: () => void): () => void {
    this.started.add(listener);
    return () => this.started.delete(listener);
  }

  /**
   * Opens a permanent media lane from a user gesture. Internet radio needs the actual
   * AudioContext so its MediaElementSource can share the game's master volume and mute.
   */
  async mediaLane(options: LaneOptions = {}): Promise<Lane | null> {
    const fresh = !this.context;
    const ctx = this.ensureContext();
    if (!ctx) return null;
    if (ctx.state !== 'running') {
      try {
        await this.resume(ctx);
      } catch {
        return null;
      }
    } else if (fresh) this.announce(ctx);
    return this.lane(options);
  }

  private async resume(ctx: AudioContext): Promise<void> {
    await ctx.resume();
    this.announce(ctx);
  }

  /** Tells whoever waits for sound that the context runs now. */
  private announce(ctx: AudioContext): void {
    if (ctx.state !== 'running') return;
    for (const listener of [...this.started]) {
      try {
        listener();
      } catch (err) {
        console.warn('a sound waiting for the context failed', err);
      }
    }
  }

  /** Master volume, 0 … 1, as the slider reads; the gain is its `perceptualGain`. */
  setVolume(volume: number): void {
    this.volume = clamp(volume, 0, 1);
    this.master?.gain.setTargetAtTime(perceptualGain(this.volume), this.now, 0.02);
  }

  getVolume(): number {
    return this.volume;
  }

  /** Silences everything without touching the volume. */
  setMuted(muted: boolean): void {
    this.muted = muted;
    this.mute?.gain.setTargetAtTime(muted ? 0 : 1, this.now, 0.02);
  }

  isMuted(): boolean {
    return this.muted;
  }

  /** Where the ears are, asked whenever a sound is placed in the world. */
  setListener(listener: (() => ListenerPose | null) | null): void {
    this.listener = listener;
  }

  /** Where the ears are now (for tests and the debug console). */
  listenerPose(): ListenerPose | null {
    return this.listener?.() ?? null;
  }

  /** Labels of the sounds playing now, oldest first (for tests and the debug console). */
  activeVoices(): string[] {
    this.prune(this.now);
    return this.slots.map((slot) => slot.label);
  }

  /**
   * Takes a slot for a sound, or returns null when there is no running context and when the bus
   * is full of sounds that matter more. `label` names the sound for stealing decisions and tests.
   */
  voice(label: string, options: VoiceOptions = {}): Voice | null {
    const ctx = this.context;
    const master = this.master;
    if (!ctx || !master || ctx.state !== 'running') return null;
    const now = ctx.currentTime;
    this.prune(now);
    const priority = options.priority ?? 'normal';
    if (this.slots.length >= this.maxVoices) {
      const victim = this.slots
        .filter((slot) => RANK[slot.priority] <= RANK[priority])
        .sort((a, b) => a.started - b.started)[0];
      if (!victim) return null;
      // Its fade-out finishes on its own; the sources are told to stop, so nothing leaks.
      victim.stop(STEAL_RELEASE);
      this.slots.splice(this.slots.indexOf(victim), 1);
    }

    const gain = ctx.createGain();
    const base = options.gain ?? 1;
    let panner: StereoPannerNode | null = null;
    let output: AudioNode = gain;
    if (options.at) {
      panner = ctx.createStereoPanner();
      gain.connect(panner);
      output = panner;
    }
    output.connect(master);
    let send: GainNode | null = null;
    if (options.reverb && this.reverb) {
      send = ctx.createGain();
      send.gain.value = options.reverb;
      output.connect(send);
      send.connect(this.reverb.input);
    }
    const apply = (at: Vec3Like, immediate: boolean) => {
      const placed = this.placement(at);
      if (immediate) {
        gain.gain.value = base * placed.gain;
        if (panner) panner.pan.value = placed.pan;
      } else {
        gain.gain.setTargetAtTime(base * placed.gain, ctx.currentTime, 0.03);
        panner?.pan.setTargetAtTime(placed.pan, ctx.currentTime, 0.03);
      }
    };
    if (options.at) apply(options.at, true);
    else gain.gain.value = base;

    const sources: AudioScheduledSourceNode[] = [];
    const slot: Slot = {
      label,
      priority,
      started: now,
      endsAt: now + SLOT_LIFETIME,
      stop: (release) => {
        const t = ctx.currentTime;
        gain.gain.cancelScheduledValues(t);
        gain.gain.setValueAtTime(gain.gain.value, t);
        gain.gain.linearRampToValueAtTime(0, t + release);
        for (const source of sources) {
          try {
            source.stop(t + release);
          } catch {
            // Never started, or already stopped: nothing to silence.
          }
        }
        slot.endsAt = Math.min(slot.endsAt, t + release);
      },
      free: () => {
        gain.disconnect();
        output.disconnect();
        send?.disconnect();
      },
    };
    this.slots.push(slot);
    return {
      context: ctx,
      input: gain,
      now,
      track: (source) => {
        sources.push(source);
        return source;
      },
      noise: (kind) => this.noiseBuffer(ctx, kind),
      end: (at) => {
        slot.endsAt = at;
      },
      place: (at) => apply(at, false),
      stop: (release = STEAL_RELEASE) => slot.stop(release),
    };
  }

  /**
   * A lane straight into the master mix, with its own volume, for the radio (or beside it, for the
   * ambience: `besideMaster`). It is outside the voice cap on purpose: a footstep must never steal
   * the radio, nor the radio a scream. Null until the context runs.
   */
  lane(options: LaneOptions = {}): Lane | null {
    const ctx = this.context;
    const master = this.master;
    if (!ctx || !master || ctx.state !== 'running') return null;
    const input = ctx.createGain();
    input.gain.value = clamp(options.gain ?? 1, 0, 1);
    input.connect(options.besideMaster && this.limiter ? this.limiter : master);
    let send: GainNode | null = null;
    if (options.reverb && this.reverb) {
      send = ctx.createGain();
      send.gain.value = options.reverb;
      input.connect(send);
      send.connect(this.reverb.input);
    }
    return {
      context: ctx,
      input,
      get alive() {
        return ctx.state !== 'closed';
      },
      dispose: () => {
        input.disconnect();
        send?.disconnect();
      },
    };
  }

  dispose(): void {
    for (const slot of this.slots) slot.stop(STEAL_RELEASE);
    this.slots = [];
    const ctx = this.context;
    this.context = null;
    this.master = null;
    this.mute = null;
    this.limiter = null;
    this.reverb = null;
    this.noiseBuffers.clear();
    if (ctx) void ctx.close().catch(() => undefined);
  }

  private ensureContext(): AudioContext | null {
    if (this.context || this.failed) return this.context;
    let ctx: AudioContext | null;
    try {
      ctx = this.createContext();
    } catch {
      ctx = null;
    }
    if (!ctx) {
      this.failed = true;
      return null;
    }
    try {
      // The limiter is what keeps a drum, a scream and a blast at once from clipping.
      const limiter = ctx.createDynamicsCompressor();
      limiter.threshold.value = -8;
      limiter.knee.value = 6;
      limiter.ratio.value = 12;
      limiter.attack.value = 0.003;
      limiter.release.value = 0.2;
      limiter.connect(ctx.destination);
      const mute = ctx.createGain();
      mute.gain.value = this.muted ? 0 : 1;
      mute.connect(limiter);
      const master = ctx.createGain();
      master.gain.value = perceptualGain(this.volume);
      master.connect(mute);
      this.reverb = createReverb(ctx, master);
      this.master = master;
      this.mute = mute;
      this.limiter = limiter;
      this.context = ctx;
    } catch {
      this.failed = true;
      void ctx.close().catch(() => undefined);
      return null;
    }
    return ctx;
  }

  private prune(now: number): void {
    this.slots = this.slots.filter((slot) => {
      if (slot.endsAt > now) return true;
      slot.free();
      return false;
    });
  }

  /** Pan and distance gain for a world position, relative to the listener. */
  private placement(at: Vec3Like): { pan: number; gain: number } {
    const pose = this.listener?.();
    if (!pose) return { pan: 0, gain: 1 };
    const dx = at.x - pose.position.x;
    const dy = at.y - pose.position.y;
    const dz = at.z - pose.position.z;
    const distance = Math.hypot(dx, dy, dz);
    const reference = Math.max(0.01, pose.reference);
    const gain = distance <= reference ? 1 : reference / distance;
    const pan =
      distance < 1e-6
        ? 0
        : clamp(
            ((dx * pose.right.x + dy * pose.right.y + dz * pose.right.z) / distance) * PAN_WIDTH,
            -1,
            1,
          );
    return { pan, gain };
  }

  private noiseBuffer(ctx: BaseAudioContext, kind: NoiseKind): AudioBuffer {
    let buffer = this.noiseBuffers.get(kind);
    if (!buffer) {
      buffer = createNoiseBuffer(ctx, kind, NOISE_SECONDS);
      this.noiseBuffers.set(kind, buffer);
    }
    return buffer;
  }
}
